import { request, url } from "@frontend/lib/http";

import type { ContractValues } from "../schemas/contract";
import type {
  Contract,
  ContractDraft,
  ContractOptions,
  ContractRow,
} from "../types";
import { toContractInput } from "../utils/payload";

type RequestOptions = {
  signal?: AbortSignal;
};

export type ContractApi = {
  fetchContracts(
    projectId: number,
    options?: RequestOptions,
  ): Promise<ContractRow[]>;
  fetchContract(
    projectId: number,
    contractId: number,
    options?: RequestOptions,
  ): Promise<Contract>;
  fetchDraft(
    projectId: number,
    options?: RequestOptions,
  ): Promise<ContractDraft>;
  createContract(
    projectId: number,
    input: ContractValues,
    options?: RequestOptions,
  ): Promise<Contract>;
  updateContract(
    projectId: number,
    contractId: number,
    input: ContractValues,
    options?: RequestOptions,
  ): Promise<Contract>;
  deleteContract(
    projectId: number,
    contractId: number,
    options?: RequestOptions,
  ): Promise<void>;
  fetchOptions(
    projectId: number,
    options?: RequestOptions,
  ): Promise<ContractOptions>;
  contractPdfUrl(projectId: number, contractId: number): string;
};

export const contractApi: ContractApi = {
  fetchContracts(projectId, options) {
    return request({
      route: "api_project_contracts_list",
      params: { projectId },
      ...options,
    });
  },

  fetchContract(projectId, contractId, options) {
    return request({
      route: "api_project_contracts_show",
      params: { projectId, contractId },
      ...options,
    });
  },

  fetchDraft(projectId, options) {
    return request({
      route: "api_project_contracts_draft",
      params: { projectId },
      ...options,
    });
  },

  createContract(projectId, input, options) {
    return request({
      route: "api_project_contracts_create",
      params: { projectId },
      method: "POST",
      body: { contractInput: toContractInput(input) },
      ...options,
    });
  },

  updateContract(projectId, contractId, input, options) {
    return request({
      route: "api_project_contracts_update",
      params: { projectId, contractId },
      method: "PUT",
      body: { contractInput: toContractInput(input) },
      ...options,
    });
  },

  deleteContract(projectId, contractId, options) {
    return request({
      route: "api_project_contracts_delete",
      params: { projectId, contractId },
      method: "DELETE",
      ...options,
    });
  },

  fetchOptions(projectId, options) {
    return request({
      route: "api_project_contract_options",
      params: { projectId },
      ...options,
    });
  },

  contractPdfUrl(projectId, contractId) {
    return url("api_project_contracts_pdf", { projectId, contractId });
  },
};
